import { useEffect, useState } from "react";
import { Col, Row } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useStyleUtils from "../../utilities/styles";
import Status from "../../enums/Status";
import Error from './Error';
import Placeholder from "../../navigation/Placeholder";
import IFolder from "../../models/IFolder";
import folderService from "../../services/folderService";
import FolderCard from "../folderCard/FolderCard";
import NewFolder from "../newFolder/NewFolder";
import PasswordList from "../../passwords/list/PasswordList";
import FolderHeader from "../header/FolderHeader";

export default function Folders() {
    const { t } = useTranslation();

    const { folderId } = useParams();

    const navigate = useNavigate();
    const style = useStyleUtils();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [folders, setFolders] = useState<IFolder[]>([]);
    const [folder, setFolder] = useState<IFolder>(null!);

    const handleFolderClick = (id: string) => navigate(`/f/${id}`);

    const load = () => {
        setStatus(Status.LOADING);
    
        loadData(folderId!, setFolders, setFolder)
            .then(() => setStatus(Status.LOAD_SUCCESS))
            .catch(() => setStatus(Status.LOAD_ERROR));
      };
    
      const handleSave = () => load();

      const handleDelete = () => {
        const path = folder.parentId 
            ? `/f/${folder.parentId}`
            : "/";

        navigate(path);
      };
    
      useEffect(load, [folderId]);

    return (
        <Row>
            {
                status === Status.LOADING &&
                <Placeholder />
            }
            {
                status === Status.LOAD_ERROR &&
                <Error />
            }
            {
                status === Status.LOAD_SUCCESS &&
                <Col>
                    {
                        folder &&
                        <FolderHeader folder={folder} onSave={handleSave} onDelete={handleDelete} />
                    }
                    {
                        !folder &&
                        <h2 className={style.mt0}>
                            {t("folders.titles.list.shared")}
                        </h2>
                    }
                    <Row>
                        {
                            folders
                                .filter(f => (!!folder && folder.isPersonal) || !f.isPersonal)
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((f) => (
                                    <Col xs={12} md={6} lg={4} key={f.id}>
                                        <FolderCard folder={f} onClick={handleFolderClick} />
                                    </Col>
                                ))
                        }
                        <Col xs={12} md={6} lg={4}>
                            <NewFolder parentFolderId={folderId} onSave={handleSave} isPersonal={!!folder && folder.isPersonal} />
                        </Col>
                    </Row>
                    {
                        folder && <PasswordList isPersonalFolder={folder.isPersonal} />
                    }
                    {
                        !folder &&
                        <Row>
                            <Col>
                                <h2>{t("folders.titles.list.my")}</h2>
                                <Row>
                                    {
                                        folders
                                            .filter(folder => folder.isPersonal)
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map((folder) => (
                                                <Col xs={12} md={6} lg={4} key={folder.id}>
                                                    <FolderCard folder={folder} onClick={handleFolderClick} />
                                                </Col>
                                            ))
                                    }
                                    <Col xs={12} md={6} lg={4}>
                                        <NewFolder parentFolderId={folderId} onSave={handleSave} isPersonal={true} />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    }
                </Col>
            }
        </Row>
    );
}

async function loadData(
    folderId: string,
    setFolders: (folders: IFolder[]) => void,
    setFolder: (folder: IFolder) => void
) {
    const folders = await folderService.getFolders(folderId);
    setFolders(folders);

    if (!folderId) {
        setFolder(null!);
        return;
    }

    const folder = await folderService.getFolderById(folderId);
    setFolder(folder!);
}